import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import WarningIcon from "@mui/icons-material/Warning";
import Cookies from "js-cookie";
import {
  getMicrosoftUserInfo
} from "../../services/apiService";

const UserProfile = ({ isAuthenticated }) => {
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [showSessionTimeoutDialog, setShowSessionTimeoutDialog] = useState(false);
  const [userInfo, setUserInfo] = useState({
    accessToken: '',
    userId: '',
    email: '',
    full_name: '',
    persona: '',
  });
  // const [userInfoSSO, setUserInfoSSO] = useState({});
  const displayDevLabel = process.env.REACT_APP_DISPLAY_DEV_LABEL;

  useEffect(() => {
    // Listen for personaSet event to update user info
    const handlePersonaSet = (event) => {
      if (event.detail) {
        setUserInfo({
          accessToken: event.detail.accessToken || '',
          userId: event.detail.userId || '',
          email: event.detail.email || '',
          full_name: event.detail.full_name || '',
          persona: event.detail.persona || '',
        });
      }
    };
    window.addEventListener('personaSet', handlePersonaSet);
    return () => {
      window.removeEventListener('personaSet', handlePersonaSet);
    };
  }, []);
  const [userInfoSSO, setUserInfoSSO] = useState({});

  useEffect(() => {
    if (isAuthenticated) {
      // Check token expiration
      checkTokenExpiration();
      
      // Fetch Microsoft user info if user is authenticated via Microsoft
      const fetchMicrosoftUserInfo = async () => {
        const access_token = localStorage.getItem('msft_access_token');
        if (access_token) {
          try {
            const msUserInfo = await getMicrosoftUserInfo(access_token);
            // console.log('User Info from Microsoft Graph:', msUserInfo);
            setUserInfoSSO(msUserInfo || {});
          } catch (error) {
            console.error('Failed to fetch Microsoft user info:', error);
            setUserInfoSSO({});
          }
        }
      };
      
      fetchMicrosoftUserInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userInfo.persona]);

  // Check token expiration periodically
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        checkTokenExpiration();
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const decodeToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  const checkTokenExpiration = () => {
    const accessToken = localStorage.getItem('accessToken');
    const msftAccessToken = localStorage.getItem('msft_access_token');
    
    // Check main access token
    if (accessToken) {
      const decoded = decodeToken(accessToken);
      if (decoded && decoded.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        if (decoded.exp < currentTime) {
          console.log('Main access token expired, showing session timeout dialog');
          // setShowSessionTimeoutDialog(true);
          handleSignOut(); // Automatically sign out on token expiration
          return;
        }
      }
    }

    // Check Microsoft access token
    if (msftAccessToken) {
      const decoded = decodeToken(msftAccessToken);
      if (decoded && decoded.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        if (decoded.exp < currentTime) {
          console.log('Microsoft access token expired, showing session timeout dialog');
          // setShowSessionTimeoutDialog(true);
          handleSignOut(); // Automatically sign out on token expiration
          return;
        }
      }
    }
  };

  const handleSessionTimeoutContinue = () => {
    setShowSessionTimeoutDialog(false);
    
    // Check if user was authenticated via Microsoft and redirect to login
    const msftAccessToken = localStorage.getItem('msft_access_token');
    const hasMicrosoftAuth = msftAccessToken || userInfo.persona === 'microsoft';
    
    if (hasMicrosoftAuth) {
      // For Microsoft users, redirect to Microsoft login
      window.location.href = window.location.origin + '?mslogin=true';
    } else {
      // For other users, reload to trigger regular login
      window.location.reload();
    }
  };

  const handleSessionTimeoutLogout = () => {
    setShowSessionTimeoutDialog(false);
    
    // Check if user was authenticated via Microsoft
    const msftAccessToken = localStorage.getItem('msft_access_token');
    const hasMicrosoftAuth = msftAccessToken || userInfo.persona === 'microsoft';
    
    if (hasMicrosoftAuth) {
      // Perform Microsoft logout
      handleMicrosoftSignOut();
    } else {
      // Regular logout
      performLocalSignOut();
      window.location.reload();
    }
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleSignOut = () => {
    setUserMenuAnchorEl(null);

    // Check if user was authenticated via Microsoft
    const msftAccessToken = localStorage.getItem('msft_access_token');
    const hasMicrosoftAuth = msftAccessToken || userInfo.persona === 'microsoft';

    if (hasMicrosoftAuth) {
      // Perform Microsoft logout first
      handleMicrosoftSignOut();
    } else {
      // Regular logout for non-Microsoft users
      performLocalSignOut();
    }
  };

  const handleMicrosoftSignOut = () => {
    const tenantId = process.env.REACT_APP_MICROSOFT_TENANT_ID;

    // Clear local storage first
    performLocalSignOut();
    

    // Redirect to Microsoft logout endpoint if tenant is configured (without redirect back)
    if (tenantId) {
      const logoutUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout`;
      window.location.href = logoutUrl;
    } else {
      // If Microsoft config is not available, just reload
      window.location.reload();
    }
  };

  const performLocalSignOut = () => {
    // Clear all authentication-related data
    localStorage.removeItem('authenticated');
    localStorage.removeItem('persona');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('followsession_id');
    localStorage.removeItem('enrichsession_id');
    localStorage.removeItem('plan_generation_id');
    localStorage.removeItem('question_id');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userFullName');
    localStorage.removeItem('userId');
    localStorage.removeItem('msft_access_token');
    localStorage.removeItem('msft_refresh_token');
    localStorage.removeItem('msft_auth_state');
    localStorage.removeItem('msft_code_verifier');
    localStorage.removeItem('previousFiles');
    localStorage.removeItem('notifications');

    // Clear cookies
    Cookies.remove("chatMessages", { path: "/" });

    // Clear user info state
    setUserInfo({
      accessToken: '',
      userId: '',
      email: '',
      full_name: '',
      persona: '',
    });
    window.location.reload();
  };

  const getUserInitials = (fullName, email) => {
    if (fullName) {
      const parts = fullName.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      } else if (parts.length === 1) {
        return parts[0][0].toUpperCase();
      }
    }
    if (email) return email[0].toUpperCase();
  };

  const getUserDisplayName = () => {
    return userInfo.full_name || userInfo.email || 'User';
  };

  return (
    <>
      <Tooltip title="User Profile">
        <IconButton
          onClick={handleUserMenuOpen}
          sx={{
            p: 0.5,
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.1)",
            },
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: "#1976d2",
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            {getUserInitials(userInfo.full_name, userInfo.email)}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={userMenuAnchorEl}
        open={Boolean(userMenuAnchorEl)}
        onClose={handleUserMenuClose}
        onClick={handleUserMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            border: '1px solid #DDE5F4',
            borderRadius: '14px',
            boxShadow: '0 16px 30px rgba(18, 36, 84, 0.18)',
            mt: 2.2,
            mr: 0.5,
            minWidth: 276,
            maxWidth: 320,
            py: 0.6,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: 0,
              mr: 1.2,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 18,
              width: 13,
              height: 13,
              bgcolor: '#ffffff',
              borderTop: '1px solid #DDE5F4',
              borderLeft: '1px solid #DDE5F4',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          disabled
          sx={{
            opacity: '1 !important',
            alignItems: 'flex-start',
            px: 1.6,
            py: 1.35,
            borderBottom: '1px solid #E5ECF8',
            background: 'linear-gradient(180deg, #F9FBFF 0%, #F3F7FF 100%)',
            '&.Mui-disabled': {
              opacity: 1,
            },
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: "#9EC4EA",
              color: '#FFFFFF',
              fontSize: "0.875rem",
              fontWeight: 700,
            }}
          >
            {getUserInitials(userInfo.full_name, userInfo.email)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: 700,
                color: '#4E5B78',
                fontFamily: "'Poppins', sans-serif",
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {getUserDisplayName()}
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: '#A6AFC4',
                fontFamily: "'Poppins', sans-serif",
                fontSize: '0.84rem',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {userInfo.email}
            </Typography>
            {userInfoSSO.jobTitle && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', color: '#8A97B6' }}>
                {userInfoSSO.jobTitle}
              </Typography>
            )}
            {userInfoSSO.officeLocation && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', color: '#8A97B6' }}>
                {userInfoSSO.officeLocation}
              </Typography>
            )}
          </Box>
        </MenuItem>

        <MenuItem
          onClick={handleUserMenuClose}
          sx={{
            mx: 0.8,
            mt: 0.7,
            mb: 0.35,
            borderRadius: '10px',
            px: 1.2,
            py: 1,
            '&:hover': {
              backgroundColor: '#F3F7FF',
            },
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" sx={{ color: '#233D87' }} />
          </ListItemIcon>
          <ListItemText
            primary="Profile"
            primaryTypographyProps={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: '0.98rem',
              fontWeight: 600,
              color: '#1F2F5A',
            }}
          />
        </MenuItem>

        <MenuItem
          onClick={handleSignOut}
          sx={{
            mx: 0.8,
            mb: 0.35,
            borderRadius: '10px',
            px: 1.2,
            py: 1,
            '&:hover': {
              backgroundColor: '#FFF2F2',
            },
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: '#233D87' }} />
          </ListItemIcon>
          <ListItemText
            primary="Sign Out"
            primaryTypographyProps={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: '0.98rem',
              fontWeight: 600,
              color: '#1F2F5A',
            }}
          />
        </MenuItem>
      </Menu>
      
      {/* Session Timeout Dialog */}
      <Dialog
        open={showSessionTimeoutDialog}
        onClose={() => {}} // Prevent closing by clicking outside
        disableEscapeKeyDown // Prevent closing with escape key
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: '#d32f2f',
          }}
        >
          <WarningIcon color="error" />
          Session Timeout
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your session has expired due to inactivity.
          </Alert>
          <Typography variant="body1">
            You can either continue with a new session or logout completely.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleSessionTimeoutLogout}
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
          >
            Logout
          </Button>
          <Button
            onClick={handleSessionTimeoutContinue}
            variant="contained"
            color="primary"
            autoFocus
          >
            Continue Session
          </Button>
        </DialogActions>
      </Dialog>

      {displayDevLabel === 'true' && (
        <Box
          title="Development Environment"
          sx={{
            fontStyle: "italic",
            color: "rgb(186 32 37)",
            fontSize: "12px",
            textAlign: "center",
          }}
        >
          *Development Environment
        </Box>
      )}
    </>
  );
};

UserProfile.propTypes = {
  isAuthenticated: PropTypes.bool
};

export default UserProfile;
